import api from './api';

interface Device {
  id: number;
  device_name: string;
  device_model?: string;
  os_type?: string;
  last_login: string;
  created_at: string;
}

interface PairingToken {
  pairing_token: string;
  expires_at: string;
  device_id: number;
}

/**
 * Get all devices paired with the current user's account
 */
export const getUserDevices = async (): Promise<Device[]> => {
  const response = await api.get('/mobile-devices');
  return response.data;
};

/**
 * Generate a new pairing token for the current user
 */
export const generatePairingToken = async (): Promise<PairingToken> => {
  const response = await api.post('/mobile-devices/pairing-token');
  return response.data;
};

/**
 * Delete (unpair) a device from the current user's account
 */
export const deleteDevice = async (deviceId: number): Promise<void> => {
  try {
    await api.delete(`/mobile-devices/${deviceId}`);
  } catch (error: any) {
    console.error('Error deleting device:', error);
    throw error;
  }
};

/**
 * Update a device's information
 */
export const updateDevice = async (
  deviceId: number, 
  data: { device_name?: string; is_active?: boolean }
): Promise<Device> => {
  const response = await api.patch(`/mobile-devices/${deviceId}`, data);
  return response.data;
};

/**
 * Update a device's push notification token
 */
export const updatePushToken = async (
  deviceId: string,
  pushToken: string
): Promise<void> => {
  await api.post('/mobile-devices/push-token', {
    device_id: deviceId,
    push_token: pushToken,
  });
};

/**
 * Verify a QR code pairing token
 */
export const verifyPairingToken = async (
  pairingToken: string,
  deviceId: string
): Promise<{ valid: boolean; user: { id: number; email: string; name: string } }> => {
  const response = await api.post('/mobile-auth/verify-pairing-token', {
    pairing_token: pairingToken,
    device_id: deviceId,
  });
  return response.data;
};

/**
 * Complete the device pairing process
 */
export const pairDevice = async (
  pairingToken: string,
  deviceData: {
    device_id: string;
    device_name: string;
    device_model?: string;
    os_type?: string;
    os_version?: string;
    app_version?: string;
    push_token?: string;
  }
): Promise<{ 
  user: any;
  accessToken: string;
  refreshToken: string;
  device: { id: number; device_name: string };
}> => {
  const response = await api.post('/mobile-auth/pair-device', {
    pairing_token: pairingToken,
    ...deviceData,
  });
  return response.data;
};

/**
 * Login with device credentials (for mobile app)
 */
export const loginWithDevice = async (
  email: string,
  password: string,
  deviceData: {
    device_id: string;
    device_name: string;
    device_model?: string;
    os_type?: string;
    os_version?: string;
    app_version?: string;
    push_token?: string;
  }
): Promise<{
  user: any;
  accessToken: string;
  refreshToken: string;
  device: { id: number; device_name: string };
}> => {
  const response = await api.post('/mobile-auth/login', {
    email,
    password,
    ...deviceData,
  });
  return response.data;
};

/**
 * Refresh the mobile authentication token
 */
export const refreshMobileToken = async (
  refreshToken: string,
  deviceId: string
): Promise<{ accessToken: string; refreshToken: string }> => {
  const response = await api.post('/mobile-auth/refresh-token', {
    refreshToken,
    device_id: deviceId,
  });
  return response.data;
};

const mobileDeviceService = {
  getUserDevices,
  generatePairingToken,
  deleteDevice,
  updateDevice,
  updatePushToken,
  verifyPairingToken,
  pairDevice,
  loginWithDevice,
  refreshMobileToken,
};

export default mobileDeviceService;